import React from 'react';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { Alert, Box, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import GlobalPortfolioHeader from './features/portfolio/components/GlobalPortfolioHeader';
import PortfolioHealthScore from './features/portfolio/components/PortfolioHealthScore';
import StressTestsAlerts from './features/portfolio/components/StressTestsAlerts';
import FearGreedScore from './features/macro/components/FearGreedScore';
import DominanceScore from './features/macro/components/DominanceScore';
import { usePortfolioStore } from '../src/stores/portfolioStore';
import { usePortfolioStore as usePortfolioStoreUnified } from './stores/portfolioStore';
import { useExchangeStore } from './features/portfolio/stores/exchangeStore';
import { useMacroMarketStore } from './features/macro/stores/macroMarketStore';
// Removed unused import for getDominanceHistory
// ...existing code...
import MainAiInsight from './features/insights/MainAiInsight';

type SectionIntroProps = {
  eyebrow: string;
  title: string;
  description: string;
};

const SectionIntro: React.FC<SectionIntroProps> = ({ eyebrow, title, description }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em' }}>
      {eyebrow}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', mb: 0.7 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8, maxWidth: 760 }}>
      {description}
    </Typography>
  </Box>
);

const DashboardUltraPremium = () => {
  // Fetch methods from Zustand stores
  const fetchPortfolio = usePortfolioStore(s => s.fetchPortfolio);
  const fetchExchanges = useExchangeStore(s => s.fetchExchanges);
  const fetchMacroMarket = useMacroMarketStore(s => s.fetchMacroMarket);

  React.useEffect(() => {
    // Appel forcé du bon fetchPortfolio pour diagnostic
    import('../src/stores/portfolioStore').then(mod => {
      if (mod.usePortfolioStore) {
        const forcedFetch = mod.usePortfolioStore.getState().fetchPortfolio;
        if (forcedFetch) forcedFetch();
      }
    });
    fetchPortfolio && fetchPortfolio();
    fetchExchanges && fetchExchanges();
    fetchMacroMarket && fetchMacroMarket();
  }, [fetchPortfolio, fetchExchanges, fetchMacroMarket]);
  // Portfolio stats
  // ...existing code...
  const stressTests = usePortfolioStore(s => s.stressTests);
  // log supprimé
  // Récupère toutes les positions unifiées (manual, exchange, onchain)
    const unifiedPositions = React.useMemo(() => {
      return usePortfolioStoreUnified.getState().getUnifiedPositions ? usePortfolioStoreUnified.getState().getUnifiedPositions() : [];
    }, []);
  // ...existing code...
  // ...existing code...

  // Health score logic (unified positions)
  let healthScore = 0;
  // healthLabel and healthInterpretation will be computed in PortfolioHealthScore
  if (Array.isArray(unifiedPositions) && unifiedPositions.length > 0) {
    // Utilise value si présent, sinon price * quantity
    const getPosValue = (p: any) =>
      typeof p.value === 'number' ? p.value :
      (typeof p.price === 'number' && typeof p.quantity === 'number' ? p.price * p.quantity : 0);
    const total = unifiedPositions.reduce((sum, p) => sum + getPosValue(p), 0);
    const allocations = unifiedPositions.map(p => ({
      assetId: p.assetId,
      percent: total > 0 ? getPosValue(p) / total * 100 : 0
    }));
    if (total > 0 && allocations.length > 0) {
      const maxPct = Math.max(...allocations.map(a => a.percent));
      healthScore = Math.max(0, Math.min(100, 100 - maxPct));
      // healthLabel and healthInterpretation will be computed in PortfolioHealthScore
    } else {
      // Si total = 0 ou allocations vides, score = 0
      healthScore = 0;
      // healthLabel and healthInterpretation will be computed in PortfolioHealthScore
    }
  }

  // ...existing code...

  // Macro market
  const macro = useMacroMarketStore();
  // ...existing code...
  // DominanceScore will handle btc/eth dominance and regime extraction


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top left, rgba(228,197,114,0.18) 0%, rgba(228,197,114,0) 28%), linear-gradient(180deg, #f8f4ec 0%, #f2ede3 52%, #ebe5db 100%)',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 6,
            p: { xs: 3, md: 5 },
            mb: 4,
            background: 'linear-gradient(135deg, #10252b 0%, #214550 38%, #43736f 100%)',
            color: '#f8f4ec',
          }}
        >
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} justifyContent="space-between" alignItems="stretch">
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                label="Dashboard"
                sx={{ mb: 2, bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', letterSpacing: '0.08em', fontWeight: 700 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 760 }}>
                Un dashboard plus lisible.
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(248,244,236,0.82)', maxWidth: 700, lineHeight: 1.75 }}>
                Centré sur le signal avant le détail
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                <Chip label="Pilotage portefeuille" sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
                <Chip label="Insight IA prioritaire" sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
                <Chip label="Macro et stress tests" sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }} />
              </Stack>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                background: 'rgba(248,244,236,0.08)',
                border: '1px solid rgba(248,244,236,0.14)',
                backdropFilter: 'blur(10px)',
                minWidth: { xl: 360 },
              }}
            >
              <Stack spacing={2.1}>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <InsightsRoundedIcon sx={{ mt: 0.2, color: '#f3c65c' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Lecture prioritaire du portefeuille
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Le centre de commande et le score de santé apparaissent avant les panneaux plus secondaires.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <TimelineRoundedIcon sx={{ mt: 0.2, color: '#8fd6b1' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Climat macro séparé
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Fear & Greed et dominance BTC/ETH sont isolés dans une zone dédiée, plus simple à lire.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <SecurityRoundedIcon sx={{ mt: 0.2, color: '#f8f4ec' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Stress tests en dernier niveau
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Les alertes restent visibles, mais après la synthèse stratégique et l’insight principal.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          Le dashboard privilégie la lecture stratégique et les signaux macro. Le détail opératoire de l’allocation reste mieux servi dans la page portefeuille unifié.
        </Alert>

        <Stack spacing={4}>
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
            <Box sx={{ flex: 1.4, minWidth: 0 }}>
              <SectionIntro
                eyebrow="Centre de Commande"
                title="Valeur totale et lecture du portefeuille"
                description="Le portefeuille s’ouvre sur une synthèse haute densité: valeur, score, exposition principale et dernier état de synchronisation."
              />
              <GlobalPortfolioHeader />
            </Box>
            <Box sx={{ flex: 0.9, minWidth: 0 }}>
              <SectionIntro
                eyebrow="Health Check"
                title="Score de santé et niveau de concentration"
                description="Ce bloc garde la lecture du risque structurel à côté du centre de commande, pour rendre le niveau de concentration immédiatement visible."
              />
              <PortfolioHealthScore healthScore={healthScore} />
            </Box>
          </Stack>

          <Box sx={{ minWidth: 0 }}>
            <SectionIntro
              eyebrow="Insight IA"
              title="Signal principal à traiter"
              description="Le dashboard garde un seul insight central clairement exposé, avec contexte, analyse et action recommandée dans un même bloc."
            />
            <MainAiInsight />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <SectionIntro
              eyebrow="Climat Macro"
              title="Sentiment marché et dominance"
              description="Les métriques macro restent groupées dans une même bande pour séparer le contexte de marché du diagnostic purement portefeuille."
            />
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
              <FearGreedScore />
              <DominanceScore macro={macro} />
            </Stack>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <SectionIntro
              eyebrow="Stress Tests"
              title="Alertes et scénarios de tension"
              description="Cette dernière section garde les scénarios défavorables et les alertes de portefeuille visibles sans leur donner le premier mot dans la lecture de page."
            />
            <StressTestsAlerts tests={stressTests} />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default DashboardUltraPremium;
